import styles from './truckPreparationForm.module.css'
import { useEffect, useMemo, useState } from "react"
import { DataTypeForm2, describeHelpers } from "../../../../features/data-types"
import { Action, FormField, FieldType, ModalId, PanelWrapper, useGetCrumbs, useUIActions, Section, FormSeparator } from "../../../../features/ui"
import { Roadmap, useRoadmapHooks, RouteJourney } from "../../../logistics"
import { useOperationLoaders, operationHelpers } from "../../../operations"
import { RoutePath, useRoutesHooks } from "../../../../features/routes"
import { useNavigate } from "react-router-dom"
import { Address, addressHelpers } from '../../../../features/address'
import { useActions } from './actions'
import { LoadShipmentItemsTable } from './loadShipmentItemsTable'
import { SaleOperation } from '../../../sales'
import { Delivery } from '../../../deliveries'
import { DownloadTruckPreparationPDFModal } from '../download-truck-preparation-pdf-modal/downloadTruckPreparationPDFModal'

export const TruckPreparationForm = () => {
    const { dataType } = useRoutesHooks().parseUrl()
    const roadmap = dataType! as Roadmap
    
    const [shipments, setShipments] = useState<Delivery[]>([])
    const [operations, setOperations] = useState<SaleOperation[]>([])
    const [shipmentAddresses, setShipmentAddresses] = useState<Address[]>([])
    const [routeAddresses, setRouteAddresses] = useState<Address[]>([])
    // Products the user ticked as loaded on the truck (shipment id + item id)
    const [loadedKeys, setLoadedKeys] = useState<string[]>([])
    const { getRouteData } = useRoadmapHooks()
    const { describeAddress } = addressHelpers
    const navigate = useNavigate()
    
    const { describeCode } = describeHelpers
    const { describeCode: describeDeliveryCode } = operationHelpers
    const operationLoaders = useOperationLoaders()

    const { submitRoadmap } = useActions()
    const { openModal } = useUIActions()
    
    const load = async () => {
        const routeData = await getRouteData(roadmap)
        setShipments(routeData.shipments)
        setShipmentAddresses(routeData.shipmentAddresses)
        setRouteAddresses(Array.from(routeData.routeAddressesData.values()))
        const { loadDeliveriesOperations } = operationLoaders()
        const loadedOperations = await loadDeliveriesOperations(routeData.shipments) as SaleOperation[]
        setOperations(loadedOperations)
    }

    useEffect(() => {
        load()
    }, [roadmap])

    const roadmapLabel = `Hoja de Ruta ${describeCode(roadmap)}`
    const title = `Preparar Camión: ${roadmapLabel}`
    const crumbs = useGetCrumbs(
        'Preparación de Envíos',
        RoutePath.SHIPMENT_PREPARATION,
        roadmapLabel,
        roadmap.id,
        'Preparar Camión'
    )

    const routeAddressStrings = useMemo(() => routeAddresses.map(address => describeAddress(address)), [routeAddresses])

    const fields: FormField[] = [
        {
            name: 'journey-separator',
            type: FieldType.TEXT,
            render: () => <FormSeparator title="Trayecto" />
        },
        {
            name: 'journey',
            type: FieldType.TEXT,
            render: () => <RouteJourney sortedAddresses={routeAddressStrings} />
        },
        {
            name: 'shipments-separator',
            type: FieldType.TEXT,
            render: () => <FormSeparator title="Orden de Carga" className={styles.separator} />
        },
        {
            name: 'shipments',
            type: FieldType.TEXT,
            label: ' ',
            hint: 'Cargar los productos en el camión teniendo en cuenta el siguiente orden:',
            loading: shipments.length === 0 || operations.length === 0,
            render: () => (
                <div className={styles.shipmentsContainer}>
                    {[...shipments].reverse().map((shipment, index) => {
                        const address = shipmentAddresses.find(address => address.id === shipment.addressId)!
                        const title = `${index + 1}) Envío ${describeDeliveryCode(shipment)}: ${describeAddress(address)}`
                        const operation = operations.find(operation => operation.id === shipment.operationId)!
                        return (
                            <Section
                                key={shipment.id}
                                id={shipment.id}
                                title={title}
                            >
                                <LoadShipmentItemsTable
                                    operation={operation}
                                    shipment={shipment}
                                    isLoaded={itemId => isLoaded(shipment.id, itemId)}
                                    onToggleLoaded={(itemId, loaded) => onToggleLoaded(shipment.id, itemId, loaded)}
                                />
                            </Section>
                        )
                    })}
                </div>  
            )
        }
    ]
    
    const loadedKey = (shipmentId: string, itemId: string) => `${shipmentId}-${itemId}`
    const isLoaded = (shipmentId: string, itemId: string) => loadedKeys.includes(loadedKey(shipmentId, itemId))
    const onToggleLoaded = (shipmentId: string, itemId: string, loaded?: boolean) => {
        const key = loadedKey(shipmentId, itemId)
        setLoadedKeys(keys => loaded ? [...keys, key] : keys.filter(currentKey => currentKey !== key))
    }

    // Ticking is optional: if some product isn't ticked, the user is just asked whether everything was loaded
    const warn = async () => {
        const itemsAmount = shipments.flatMap(shipment => shipment.items.filter(item => item.quantity > 0)).length
        const missingAmount = itemsAmount - loadedKeys.length
        return missingAmount > 0 ?
            [`No tildaste ${missingAmount === 1 ? '1 producto' : `${missingAmount} de ${itemsAmount} productos`} como cargado${missingAmount === 1 ? '' : 's'} en el camión.`] :
            []
    }

    const onSubmit = () => {
        submitRoadmap(roadmap.id)
    }
    
    const onCancel = () => navigate(-1)

    const actions: Action[] = [
        { icon: 'printer', label: 'Imprimir', kind: 'quinary', onClick: () => openModal(ModalId.DOWNLOAD_TRUCK_PREPARATION_PDF) }
    ]

    return (
        <PanelWrapper
            title={title}
            crumbs={crumbs}
            wide
            actions={actions}
        >
            <DownloadTruckPreparationPDFModal roadmap={roadmap} />
            <DataTypeForm2
                formId="truck-preparation-form"
                fields={fields}
                createMode={false}
                updateLabel="Confirmar Preparación"
                onSubmit={onSubmit}
                onCancel={onCancel}
                warn={warn}
                warnTitle="Productos sin tildar"
                warnDescription="Revisá que hayas cargado todo en el camión antes de confirmar la preparación."
                warnSubmitLabel="Sí, cargué todo"
                warnCancelLabel="Volver a revisar"
            />
        </PanelWrapper>
    )
}
